/**
 * Feito pelo agente netexperts
 * Módulo: AnalyticsObra — painel expandido de 1 unidade de estoque (lazy load)
 */
import { useEstoqueUnidadeExpansao } from '../hooks/useObraAnalytics'
import { formatCurrency, formatDate, formatNumber, formatPercent } from '../../../../utils/obraAnalytics'

const SituacaoBadge = ({ situacao }) => {
    const map = {
        vencido: 'bg-rose-100 text-rose-700',
        a_vencer: 'bg-amber-100 text-amber-700',
        sem_venc: 'bg-gray-100 text-gray-600',
    }
    const label = { vencido: 'Vencido', a_vencer: 'A vencer', sem_venc: 'Sem venc.' }[situacao] || situacao
    return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${map[situacao] || map.sem_venc}`}>{label}</span>
}

const MiniTable = ({ headers, rows, empty }) => (
    <div className="overflow-x-auto">
        {rows.length === 0 ? (
            <p className="text-xs text-gray-400 py-2">{empty}</p>
        ) : (
            <table className="min-w-full text-xs">
                <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wide text-gray-400 border-b border-gray-100">
                        {headers.map((h) => <th key={h} className="py-1.5 pr-3 font-medium">{h}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => (
                        <tr key={i} className="border-b border-gray-50 last:border-0">
                            {row.map((cell, j) => <td key={j} className="py-1.5 pr-3 text-gray-600">{cell}</td>)}
                        </tr>
                    ))}
                </tbody>
            </table>
        )}
    </div>
)

const EstoqueUnidadeExpansao = ({ obraId, row, investidor, period, enabled, cacheKey, cached, onLoaded }) => {
    const requestParams = {
        id: row?.id,
        identificador: row?.identificador,
        fim: period?.fim,
    }
    const { data, loading, error } = useEstoqueUnidadeExpansao(obraId, requestParams, investidor, {
        enabled,
        cached,
        onLoaded: (key, res) => onLoaded?.(cacheKey ?? key, res),
    })

    if (loading) {
        return <div className="px-4 py-5 text-xs text-gray-400 text-center">Carregando detalhes da unidade…</div>
    }
    if (error) {
        return <div className="px-4 py-5 text-xs text-rose-500 text-center">Não foi possível carregar esta unidade.</div>
    }
    if (!data) return null

    const u = data.unidade
    const v = data.vinculo
    const limite = 15

    return (
        <div className="px-4 py-4 bg-emerald-50/40 border-t border-gray-100 space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="rounded-xl bg-white border border-gray-100 p-3">
                    <p className="text-[10px] uppercase text-gray-400 font-semibold">Status</p>
                    <p className="text-sm font-bold text-gray-800">{u.descricao_status}</p>
                </div>
                <div className="rounded-xl bg-white border border-gray-100 p-3">
                    <p className="text-[10px] uppercase text-gray-400 font-semibold">Valor tabela</p>
                    <p className="text-sm font-bold text-gray-800">{formatCurrency(u.valor)}</p>
                </div>
                <div className="rounded-xl bg-white border border-gray-100 p-3">
                    <p className="text-[10px] uppercase text-gray-400 font-semibold">Cadastro</p>
                    <p className="text-sm font-bold text-gray-800">{formatDate(u.data_cadastro)}</p>
                </div>
            </div>

            {!v?.encontrado ? (
                <div className="rounded-xl bg-white border border-amber-100 p-4">
                    <p className="text-sm text-amber-800">Esta unidade não possui venda vinculada no período.</p>
                </div>
            ) : (
                <>
                    <div className="rounded-xl bg-white border border-emerald-100 p-3">
                        <p className="text-xs font-semibold text-emerald-800 mb-1">Vínculo com venda</p>
                        <p className="text-xs text-gray-600">
                            Contrato <b>{v.contrato}</b> · Cliente <b>{v.cliente}</b>
                            {v.data_ven && v.data_ven !== '-' && <> · Venda em <b>{formatDate(v.data_ven)}</b></>}
                        </p>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        <div className="rounded-xl bg-white border border-gray-100 p-3">
                            <p className="text-[10px] uppercase text-emerald-600 font-semibold">Recebido</p>
                            <p className="text-sm font-bold text-emerald-700">{formatCurrency(data.recebido)}</p>
                        </div>
                        <div className="rounded-xl bg-white border border-gray-100 p-3">
                            <p className="text-[10px] uppercase text-amber-600 font-semibold">A Receber</p>
                            <p className="text-sm font-bold text-amber-700">{formatCurrency(data.a_receber)}</p>
                        </div>
                        <div className="rounded-xl bg-white border border-gray-100 p-3">
                            <p className="text-[10px] uppercase text-gray-400 font-semibold">Conversão</p>
                            <p className="text-sm font-bold text-gray-800">{formatPercent(data.pct_recebido)}</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="rounded-xl bg-white border border-gray-100 p-3">
                            <p className="text-xs font-semibold text-gray-700 mb-2">
                                Parcelas recebidas (contrato)
                                {data.recebidos.total > limite && (
                                    <span className="text-gray-400 font-normal"> — {limite} de {formatNumber(data.recebidos.total)}</span>
                                )}
                            </p>
                            <MiniTable
                                headers={['Parcela', 'Tipo', 'Competência', 'Valor', 'Depositado']}
                                rows={(data.recebidos.parcelas || []).map((p) => [
                                    p.parcela ?? '-',
                                    p.tipo ?? '-',
                                    formatDate(p.data_comp),
                                    formatCurrency(p.vlr_parcela),
                                    formatCurrency(p.total_dep),
                                ])}
                                empty="Nenhuma parcela recebida para o contrato vinculado."
                            />
                        </div>
                        <div className="rounded-xl bg-white border border-gray-100 p-3">
                            <p className="text-xs font-semibold text-gray-700 mb-2">
                                Parcelas a receber (contrato)
                                {data.aReceber.total > limite && (
                                    <span className="text-gray-400 font-normal"> — {limite} de {formatNumber(data.aReceber.total)}</span>
                                )}
                            </p>
                            <MiniTable
                                headers={['Parcela', 'Tipo', 'Vencimento', 'Valor', 'Situação']}
                                rows={(data.aReceber.parcelas || []).map((p) => [
                                    p.parcela ?? '-',
                                    p.tipo ?? '-',
                                    formatDate(p.vencimento),
                                    formatCurrency(p.vlr_parcela),
                                    <SituacaoBadge key={`${p.parcela}-s`} situacao={p.situacao} />,
                                ])}
                                empty="Nenhuma parcela em aberto para o contrato vinculado."
                            />
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}

export default EstoqueUnidadeExpansao
